import { Alert, Button, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { semanticColor } from "@how/design-system";
import { useState } from "react";
import {
  BUSY_ELSEWHERE,
  useAssignPerson,
  usePersonBusyElsewhere,
  useTeamMembers,
  type GridSlot,
} from "./services-queries";

export interface AssignModalProps {
  /** The slot to fill; undefined when closed. */
  readonly slot:
    | (GridSlot & { teamId: string; planId: string; planTitle: string; planWhen: string })
    | undefined;
  readonly onClose: () => void;
}

/** Put a member of the position's team on a date. The database refuses anyone not on the team. */
export function AssignModal({ slot, onClose }: AssignModalProps) {
  const members = useTeamMembers(slot?.teamId ?? null);
  const assign = useAssignPerson();
  const [personId, setPersonId] = useState<string | null>(null);
  const busy = usePersonBusyElsewhere(slot?.planId ?? null, personId);
  const taken = new Set((slot?.assignments ?? []).map((a) => a.personId));
  const options = (members.data ?? [])
    .filter((m) => !taken.has(m.personId))
    .map((m) => ({ value: m.personId, label: m.name }));
  const code = (assign.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "They are already on this date."
      : code === "23514"
        ? "They are not on this team, or the plan is cancelled."
        : assign.isError
          ? "Not assigned. Check your access and try again."
          : null;
  return (
    <Modal
      opened={slot !== undefined}
      onClose={onClose}
      title={slot === undefined ? "" : `${slot.position} · ${slot.planWhen}`}
      radius="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {slot === undefined ? "" : `${slot.team} team, ${slot.planTitle}.`}{" "}
          {options.length === 0 && members.data !== undefined
            ? "Everyone on the team is already on this date. Add people to the team in Groups."
            : "Choose someone on the team."}
        </Text>
        <Select
          label="Who"
          placeholder="Search the team"
          searchable
          data={options}
          value={personId}
          onChange={setPersonId}
          nothingFoundMessage="Nobody on the team by that name"
        />
        {busy.data === true ? (
          <Alert color={semanticColor("warning")} icon={<IconAlertTriangle size={16} />}>
            {BUSY_ELSEWHERE}
          </Alert>
        ) : null}
        {errorText !== null ? (
          <Text size="sm" c="red">
            {errorText}
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm" mt="xs">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={personId === null}
            loading={assign.isPending}
            onClick={() => {
              if (slot === undefined || personId === null) return;
              assign.mutate(
                { planPositionId: slot.planPositionId, personId },
                { onSuccess: onClose },
              );
            }}
          >
            Assign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
